class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    
    // Prevent form submission from causing page reloads
    const cartForm = document.getElementById('cart');
    if (cartForm) {
      cartForm.addEventListener('submit', (e) => {
        // Only allow checkout button to submit, prevent other submissions
        const submitButton = e.submitter || document.activeElement;
        if (submitButton && submitButton.name === 'checkout' && submitButton.type === 'submit') {
          // Allow checkout to proceed normally
          return true;
        }
        // Prevent any other form submissions
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
    }
    
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        const miniCartElement = document.getElementById('mini-cart');
        miniCartElement.innerHTML = this.getSectionInnerHTML(html, '.shopify-section');
        
        // Dispatch event to allow other components to initialize
        // The free-gift-selector script listens for this event
        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    // Calculate and preserve progress bar state before DOM update
    const progressBar = document.querySelector('free-sample-progress-bar');
    let preservedProgress = null;
    let preservedThresholdTwo = null;
    let preservedTextState = null;
    let preservedCartTotal = null;
    
    if (progressBar && progressBar.isConnected) {
      // Preserve current progress value from the bar element itself
      preservedProgress = progressBar.currentProgress;
      preservedThresholdTwo = progressBar.thresholdTwo;
      preservedCartTotal = progressBar.lastCartTotal || 0;
      
      // Preserve text state
      const progressText = progressBar.querySelector('.free-sample-progress-bar__text--progress');
      const successText = progressBar.querySelector('.free-sample-progress-bar__text--success');
      preservedTextState = {
        progressVisible: progressText ? progressText.style.display !== 'none' : false,
        successVisible: successText ? successText.style.display !== 'none' : false,
        progressContent: progressText ? progressText.textContent : null,
        successContent: successText ? successText.textContent : null
      };
      
      // Calculate new cart total excluding gifts
      let newCartTotal = parsedState.total_price || 0;
      const giftVariantIds = progressBar.getGiftVariantIds();
      
      if (parsedState.items && giftVariantIds.length > 0) {
        parsedState.items.forEach(item => {
          const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
          if (giftVariantIds.includes(itemVariantId)) {
            newCartTotal -= (item.line_price || 0);
          }
        });
      }
      
      // If cart total (excluding gifts) hasn't changed, don't update progress bar
      // This prevents the bar from updating when only gifts are added/removed
      if (Math.abs(newCartTotal - preservedCartTotal) < 1 && preservedProgress !== null && preservedProgress !== undefined) {
        // Cart total excluding gifts hasn't changed, preserve everything
        // Don't update the progress bar at all
      } else {
        // Cart total changed, calculate new progress
        if (preservedProgress === undefined || preservedProgress === null || preservedProgress === 0) {
          preservedProgress = Math.min((newCartTotal / progressBar.thresholdTwo) * 100, 100);
        }
        preservedCartTotal = newCartTotal;
      }
    }
    
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
    
    // Dispatch event after content is updated to allow components to initialize
    document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
    
    // Restore progress bar immediately after DOM update
    // Use multiple strategies to ensure it works
    const restoreProgressBar = () => {
      const newProgressBar = document.querySelector('free-sample-progress-bar');
      if (newProgressBar && preservedProgress !== null && preservedProgress !== undefined) {
        // Set the progress value immediately
        newProgressBar.currentProgress = preservedProgress;
        newProgressBar.lastCartTotal = preservedCartTotal;
        newProgressBar.restoreProgress();
        
        // Also update the bar element directly as a fallback
        const barElement = newProgressBar.querySelector('.free-sample-progress-bar__bar');
        if (barElement) {
          barElement.style.setProperty('--progress', `${preservedProgress}%`);
        }
        
        // Restore text state if preserved
        if (preservedTextState) {
          const progressText = newProgressBar.querySelector('.free-sample-progress-bar__text--progress');
          const successText = newProgressBar.querySelector('.free-sample-progress-bar__text--success');
          
          if (progressText && preservedTextState.progressVisible) {
            progressText.style.display = 'block';
            if (preservedTextState.progressContent) {
              progressText.textContent = preservedTextState.progressContent;
            }
          }
          if (successText && preservedTextState.successVisible) {
            successText.style.display = 'block';
            if (preservedTextState.successContent) {
              successText.textContent = preservedTextState.successContent;
            }
          }
        }
      }
    };
    
    // Try to restore immediately
    restoreProgressBar();
    
    // Also restore in next frame to catch any timing issues
    requestAnimationFrame(() => {
      restoreProgressBar();
      
      // Then dispatch cart updated event so progress bar can update smoothly
      // But the progress bar will check if cart total changed and skip update if only gifts changed
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);

// Shipping Progress Bar for UK Market
class ShippingProgressBar extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold);
    this.updateProgress = this.updateProgress.bind(this);
  }

  connectedCallback() {
    // Prevent multiple initializations
    if (this._initialized) return;
    this._initialized = true;
    
    // Listen for cart updates
    document.addEventListener('cart:updated', this.updateProgress);
    
    // Also listen for the cart drawer opened event
    document.addEventListener('cartdrawer:opened', () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (this.isConnected) {
          this.fetchCartAndUpdate();
        }
      }, 50);
    });
    
    // Initial update
    setTimeout(() => {
      if (this.isConnected) {
        this.fetchCartAndUpdate();
      }
    }, 100);
  }

  disconnectedCallback() {
    this._initialized = false;
    document.removeEventListener('cart:updated', this.updateProgress);
  }

  fetchCartAndUpdate() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateProgressBar(cart.total_price);
      })
      .catch(console.error);
  }

  updateProgress(event) {
    // Get cart data from the event or fetch it
    if (event && event.detail && event.detail.cart) {
      const cartData = typeof event.detail.cart === 'string' 
        ? JSON.parse(event.detail.cart) 
        : event.detail.cart;
      this.updateProgressBar(cartData.total_price);
    } else {
      this.fetchCartAndUpdate();
    }
  }

  updateProgressBar(totalPrice) {
    const remaining = this.threshold - totalPrice;
    const progressPercent = Math.min((totalPrice / this.threshold) * 100, 100);
    
    const amountElement = this.querySelector('.shipping-progress-bar__amount');
    const barElement = this.querySelector('.shipping-progress-bar__bar');
    
    if (!barElement) return;
    
    // Update progress bar width
    barElement.style.setProperty('--progress', `${progressPercent}%`);
    
    // Toggle success state on the container
    if (totalPrice >= this.threshold) {
      this.classList.add('is-success');
    } else {
      this.classList.remove('is-success');
      
      // Update the remaining amount in the progress message
      if (amountElement) {
        const formattedRemaining = this.formatMoney(remaining);
        amountElement.textContent = formattedRemaining;
      }
    }
  }

  formatMoney(cents) {
    // Basic money formatting - you can enhance this based on your theme's money format
    const pounds = (cents / 100).toFixed(2);
    return `€${pounds}`;
  }
}

customElements.define('shipping-progress-bar', ShippingProgressBar);

// Free Sample Progress Bar
class FreeSampleProgressBar extends HTMLElement {
  constructor() {
    super();
    const one = parseInt(this.dataset.thresholdOne, 10);
    const two = parseInt(this.dataset.thresholdTwo, 10);
    this.thresholdTwo = (!Number.isNaN(two) ? two : !Number.isNaN(one) ? one : null) || 10000;
    this.thresholdOne = !Number.isNaN(one) ? one : this.thresholdTwo;
    this.updateProgress = this.updateProgress.bind(this);
    this.currentProgress = 0; // Store current progress to preserve during updates
    this.lastCartTotal = 0; // Store last cart total (excluding gifts) for comparison
  }

  connectedCallback() {
    // Prevent multiple initializations
    if (this._initialized) return;
    this._initialized = true;
    
    // Store current progress before any updates
    this.preserveProgress();
    
    // Restore progress immediately to prevent visual reset
    this.restoreProgress();
    
    // Listen for cart updates
    document.addEventListener('cart:updated', this.updateProgress);
    
    // Also listen for the cart drawer opened event
    document.addEventListener('cartdrawer:opened', () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (this.isConnected) {
          // Preserve and restore progress immediately, then update
          this.preserveProgress();
          this.restoreProgress();
          this.fetchCartAndUpdate();
        }
      }, 50);
    });
    
    // Initial update
    setTimeout(() => {
      if (this.isConnected) {
        // Preserve progress before fetching
        this.preserveProgress();
        this.restoreProgress();
        this.fetchCartAndUpdate();
      }
    }, 100);
  }

  disconnectedCallback() {
    this._initialized = false;
    document.removeEventListener('cart:updated', this.updateProgress);
  }

  preserveProgress() {
    // Store current progress value before DOM updates
    const barElement = this.querySelector('.free-sample-progress-bar__bar');
    if (barElement) {
      // Get progress from CSS variable (inline style takes precedence)
      const progressVar = barElement.style.getPropertyValue('--progress');
      if (progressVar) {
        const parsed = parseFloat(progressVar);
        if (!isNaN(parsed)) {
          this.currentProgress = parsed;
          return; // Successfully preserved from inline style
        }
      }
      
      // Fallback: try to get from computed style
      const computedStyle = window.getComputedStyle(barElement);
      const computedProgress = computedStyle.getPropertyValue('--progress');
      if (computedProgress) {
        const parsed = parseFloat(computedProgress);
        if (!isNaN(parsed)) {
          this.currentProgress = parsed;
          return; // Successfully preserved from computed style
        }
      }
    }
    
    // Final fallback: calculate from current state if we have data
    if (this.currentProgress === undefined || this.currentProgress === null) {
      const currentPrice = parseInt(this.dataset.current) || 0;
      if (currentPrice > 0 && this.thresholdTwo > 0) {
        this.currentProgress = Math.min((currentPrice / this.thresholdTwo) * 100, 100);
      } else {
        // If no data available, keep existing progress or default to 0
        this.currentProgress = this.currentProgress || 0;
      }
    }
  }

  restoreProgress() {
    // Immediately restore progress to prevent visual reset
    const barElement = this.querySelector('.free-sample-progress-bar__bar');
    if (barElement && this.currentProgress !== undefined && this.currentProgress !== null) {
      // Always restore, even if 0, to maintain consistency
      // Set the progress value directly on the element
      barElement.style.setProperty('--progress', `${this.currentProgress}%`);
      
      // Force a reflow to ensure the style is applied
      void barElement.offsetHeight;
    }
  }

  fetchCartAndUpdate() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Calculate total excluding gift items
        let cartTotal = cart.total_price || 0;
        const giftVariantIds = this.getGiftVariantIds();
        
        if (cart.items && giftVariantIds.length > 0) {
          cart.items.forEach(item => {
            const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
            if (giftVariantIds.includes(itemVariantId)) {
              cartTotal -= (item.line_price || 0);
            }
          });
        }
        
        // Store cart total for comparison
        this.lastCartTotal = cartTotal;
        this.updateProgressBar(cartTotal);
      })
      .catch(console.error);
  }

  getGiftVariantIds() {
    // Get gift variant IDs from data attribute first
    if (this.dataset.giftVariants) {
      return this.dataset.giftVariants.split(',').filter(id => id.trim() !== '');
    }
    // Fallback: try to get from the free-gift-selector if available
    const giftSelector = document.querySelector('free-gift-selector');
    if (giftSelector && giftSelector.giftVariantIds) {
      return giftSelector.giftVariantIds;
    }
    // Return empty array if nothing found
    return [];
  }

  updateProgress(event) {
    // Preserve current progress before update
    this.preserveProgress();
    const savedProgress = this.currentProgress;
    const savedCartTotal = this.lastCartTotal || 0;
    
    // Restore progress immediately to prevent visual reset
    this.restoreProgress();
    
    // Small delay to ensure DOM is stable after updates, but restore progress first
    setTimeout(() => {
      if (!this.isConnected) return;
      
      // Restore progress again in case DOM was updated
      if (savedProgress !== undefined && savedProgress !== null) {
        this.currentProgress = savedProgress;
        this.restoreProgress();
      }
      
      // Get cart data from the event or fetch it
      if (event && event.detail && event.detail.cart) {
        const cartData = typeof event.detail.cart === 'string' 
          ? JSON.parse(event.detail.cart) 
          : event.detail.cart;
        
        // Calculate total excluding gift items
        let cartTotal = cartData.total_price || 0;
        const giftVariantIds = this.getGiftVariantIds();
        
        if (cartData.items && giftVariantIds.length > 0) {
          cartData.items.forEach(item => {
            const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
            if (giftVariantIds.includes(itemVariantId)) {
              cartTotal -= (item.line_price || 0);
            }
          });
        }
        
        // If cart total (excluding gifts) hasn't changed, don't update the progress bar
        // This prevents the bar from updating when only gifts are added/removed
        if (Math.abs(cartTotal - savedCartTotal) < 1) {
          // Cart total excluding gifts hasn't changed, just restore progress and don't update
          this.currentProgress = savedProgress;
          this.restoreProgress();
          return;
        }
        
        // Store the new cart total for next comparison
        this.lastCartTotal = cartTotal;
        
        // Only update if the new progress is different, to avoid unnecessary updates
        const newProgress = Math.min((cartTotal / this.thresholdTwo) * 100, 100);
        if (Math.abs(newProgress - (savedProgress || 0)) > 0.1) {
          this.updateProgressBar(cartTotal);
        } else {
          // Progress hasn't changed significantly, just ensure it's restored
          this.currentProgress = savedProgress;
          this.restoreProgress();
        }
      } else {
        this.fetchCartAndUpdate();
      }
    }, 10);
  }

  updateProgressBar(totalPrice) {
  const unlocked = totalPrice >= this.thresholdTwo;
  const progressPercent = Math.min((totalPrice / this.thresholdTwo) * 100, 100);
  
  // Store new progress
  this.currentProgress = progressPercent;
  this.lastCartTotal = totalPrice;
  
  const amountElement = this.querySelector('.free-sample-progress-bar__amount');
  const barElement = this.querySelector('.free-sample-progress-bar__bar');
  const progressText = this.querySelector('.free-sample-progress-bar__text--progress');
  const successText = this.querySelector('.free-sample-progress-bar__text--success');
  const iconOne = this.querySelector('.free-sample-progress-bar__icon--one');
  const iconTwo = this.querySelector('.free-sample-progress-bar__icon--two');
  
  if (!barElement) return;
  
  // Update progress bar width smoothly
  barElement.style.setProperty('--progress', `${progressPercent}%`);
  
  // Single free sample: one tier only (no second-sample state)
  this.classList.toggle('has-one-sample', unlocked);
  this.classList.remove('has-two-samples');
  
  const translations = this.getTranslations();
  
  if (unlocked) {
    if (progressText) progressText.style.display = 'none';
    if (successText) {
      successText.style.display = 'block';
      successText.innerHTML = translations.bandeauSuccess;
    }
  } else {
    if (progressText) progressText.style.display = 'block';
    if (successText) successText.style.display = 'none';
    const remaining = this.thresholdTwo - totalPrice;
    if (amountElement) {
      const formattedRemaining = this.formatMoney(remaining);
      amountElement.textContent = formattedRemaining;
    }
  }
  
  if (iconOne) {
    iconOne.style.opacity = unlocked ? '1' : '0.3';
  }
  if (iconTwo) {
    iconTwo.style.opacity = '0.3';
  }
}

getTranslations() {
  // Try to get language from data attribute first, then from document
  const lang = this.dataset.lang || document.documentElement.lang || 'en';
  const langCode = lang.toLowerCase().substring(0, 2);
  
  const translations = {
    nl: {
      bandeauSuccess: "Je ontvangt gratis <strong>The Bandeau</strong> bij deze bestelling!"
    },
    de: {
      bandeauSuccess: "Sie erhalten <strong>The Bandeau</strong> gratis zu dieser Bestellung!"
    },
    en: {
      bandeauSuccess: "You get a free <strong>The Bandeau</strong> with this order!"
    }
  };
  
  return translations[langCode] || translations.en;
}

  formatMoney(cents) {
    const currencySymbol = this.dataset.currencySymbol || '€';
    const amount = (cents / 100).toFixed(2);
    return `${currencySymbol}${amount}`;
  }
}

customElements.define('free-sample-progress-bar', FreeSampleProgressBar);
